import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, ModalBody, Text } from "@chakra-ui/react";
import { uniqueId } from "lodash";
import React from "react";
import { OrderFilterSetting } from "../../../../../types/order/orderQuery";
import { Callout } from "../../../../Callout";
import { AppModalFooter } from "../../../../modals/AppModalFooter";
import { BulkEditOptionType } from "./BulkEditModal";
import {
  SwitchLedgerOperationEntry,
  OperationEntry,
  SwitchAssetOperationEntry,
  ChangeWalletOperationEntry,
  AdjustTimeOperationEntry,
} from "./common";
import { AssetBulkEdit } from "./operations/AssetBulkEdit";
import { LedgerBulkEdit } from "./operations/LedgerBulkEdit";
import { TimeBulkEdit } from "./operations/TimeBulkEdit";
import { WalletBulkEdit } from "./operations/WalletBulkEdit";

export function BulkEditConfigureOperationStep({
  operationType,
  operations,
  setOperations,
  selectedIds,
  filters,
  selectedCount,
  canSubmit,
  isSubmitting,
  onBack,
}: {
  operationType: BulkEditOptionType;
  operations: OperationEntry[];
  setOperations: React.Dispatch<React.SetStateAction<OperationEntry[]>>;
  selectedIds: string[];
  filters: OrderFilterSetting;
  selectedCount?: number;
  canSubmit: boolean;
  isSubmitting: boolean;
  onBack: () => void;
}): React.ReactElement {
  return (
    <>
      <ModalBody>
        <BulkEditConfigureOperationBody
          operationType={operationType}
          operations={operations}
          setOperations={setOperations}
          selectedIds={selectedIds}
          filters={filters}
          isSubmitting={isSubmitting}
        />
      </ModalBody>

      <BulkEditConfigureOperationFooter
        selectedCount={selectedCount}
        canSubmit={canSubmit}
        isSubmitting={isSubmitting}
        onBack={onBack}
      />
    </>
  );
}

function BulkEditConfigureOperationBody({
  operationType,
  operations,
  setOperations,
  selectedIds,
  filters,
  isSubmitting,
}: {
  operationType: BulkEditOptionType;
  operations: OperationEntry[];
  setOperations: React.Dispatch<React.SetStateAction<OperationEntry[]>>;
  selectedIds: string[];
  filters: OrderFilterSetting;
  isSubmitting: boolean;
}): React.ReactElement {
  switch (operationType) {
    case "ledger":
      return (
        <BulkEditLedgerBody
          operations={operations as SwitchLedgerOperationEntry[]}
          setOperations={
            setOperations as React.Dispatch<
              React.SetStateAction<SwitchLedgerOperationEntry[]>
            >
          }
          selectedIds={selectedIds}
          filters={filters}
          isSubmitting={isSubmitting}
        />
      );

    case "asset":
      return (
        <BulkEditAssetBody
          operations={operations as SwitchAssetOperationEntry[]}
          setOperations={
            setOperations as React.Dispatch<
              React.SetStateAction<SwitchAssetOperationEntry[]>
            >
          }
          selectedIds={selectedIds}
          filters={filters}
          isSubmitting={isSubmitting}
        />
      );

    case "wallet":
      return (
        <BulkEditWalletBody
          operations={operations as ChangeWalletOperationEntry[]}
          setOperations={
            setOperations as React.Dispatch<
              React.SetStateAction<ChangeWalletOperationEntry[]>
            >
          }
          selectedIds={selectedIds}
          filters={filters}
          isSubmitting={isSubmitting}
        />
      );

    case "time":
      return (
        <BulkEditTimeBody
          operations={operations as AdjustTimeOperationEntry[]}
          setOperations={
            setOperations as React.Dispatch<
              React.SetStateAction<AdjustTimeOperationEntry[]>
            >
          }
        />
      );
  }
}

function BulkEditLedgerBody({
  operations,
  setOperations,
  selectedIds,
  filters,
  isSubmitting,
}: {
  operations: SwitchLedgerOperationEntry[];
  setOperations: React.Dispatch<
    React.SetStateAction<SwitchLedgerOperationEntry[]>
  >;
  selectedIds: string[];
  filters: OrderFilterSetting;
  isSubmitting: boolean;
}): React.ReactElement {
  return (
    <>
      <Text color="gray.600" mb={4}>
        Replace a ledger classification with another across the selected
        transactions. Any matching ledger entries will be updated.
      </Text>

      <Text color="gray.600" mb={6}>
        Add another row to apply multiple re-classifications at once.
      </Text>

      <LedgerBulkEdit
        operations={operations}
        setOperations={setOperations}
        selectedIds={selectedIds}
        filters={filters}
      />

      <AddOperationButton
        operationType="ledger"
        setOperations={
          setOperations as React.Dispatch<
            React.SetStateAction<OperationEntry[]>
          >
        }
        isSubmitting={isSubmitting}
      />
    </>
  );
}

function BulkEditAssetBody({
  operations,
  setOperations,
  selectedIds,
  filters,
  isSubmitting,
}: {
  operations: SwitchAssetOperationEntry[];
  setOperations: React.Dispatch<
    React.SetStateAction<SwitchAssetOperationEntry[]>
  >;
  selectedIds: string[];
  filters: OrderFilterSetting;
  isSubmitting: boolean;
}): React.ReactElement {
  return (
    <>
      <Text color="gray.600" mb={4}>
        Replace one asset with another across the selected transactions. Any
        matching assets will be updated.
      </Text>

      <Text color="gray.600" mb={6}>
        Add another row to replace multiple assets at once.
      </Text>

      <AssetBulkEdit
        operations={operations}
        setOperations={setOperations}
        selectedIds={selectedIds}
        filters={filters}
      />

      <AddOperationButton
        operationType="asset"
        setOperations={
          setOperations as React.Dispatch<
            React.SetStateAction<OperationEntry[]>
          >
        }
        isSubmitting={isSubmitting}
      />
    </>
  );
}

function BulkEditWalletBody({
  operations,
  setOperations,
  selectedIds,
  filters,
  isSubmitting,
}: {
  operations: ChangeWalletOperationEntry[];
  setOperations: React.Dispatch<
    React.SetStateAction<ChangeWalletOperationEntry[]>
  >;
  selectedIds: string[];
  filters: OrderFilterSetting;
  isSubmitting: boolean;
}): React.ReactElement {
  return (
    <>
      <Text color="gray.600" mb={4}>
        Move selected transactions from one data source to another.
      </Text>

      <Text color="gray.600" mb={6}>
        Add another row to move transactions between multiple data sources at
        once.
      </Text>

      <WalletBulkEdit
        operations={operations}
        setOperations={setOperations}
        selectedIds={selectedIds}
        filters={filters}
      />

      <AddOperationButton
        operationType="wallet"
        setOperations={
          setOperations as React.Dispatch<
            React.SetStateAction<OperationEntry[]>
          >
        }
        isSubmitting={isSubmitting}
      />
    </>
  );
}

function BulkEditTimeBody({
  operations,
  setOperations,
}: {
  operations: AdjustTimeOperationEntry[];
  setOperations: React.Dispatch<
    React.SetStateAction<AdjustTimeOperationEntry[]>
  >;
}): React.ReactElement {
  return (
    <>
      <Text color="gray.600" mb={4}>
        Shift the date on selected transactions forward or backward by hours,
        minutes, or seconds.
      </Text>

      <Callout mb={6} maxW="30em">
        This edit is useful when transactions were originally imported in the
        wrong timezone, and need to be updated all at once.
      </Callout>

      <TimeBulkEdit operations={operations} setOperations={setOperations} />
    </>
  );
}

function BulkEditConfigureOperationFooter({
  selectedCount,
  canSubmit,
  isSubmitting,
  onBack,
}: {
  selectedCount?: number;
  canSubmit: boolean;
  isSubmitting: boolean;
  onBack: () => void;
}): React.ReactElement {
  return (
    <AppModalFooter>
      <Button
        type="button"
        variant="outline"
        colorScheme="gray"
        onClick={onBack}
        isDisabled={isSubmitting}
      >
        Back
      </Button>

      <Button
        colorScheme="primary"
        isDisabled={!canSubmit || isSubmitting}
        isLoading={isSubmitting}
        type="submit"
      >
        Update {selectedCount?.toLocaleString()} transactions
      </Button>
    </AppModalFooter>
  );
}

function AddOperationButton({
  operationType,
  setOperations,
  isSubmitting,
}: {
  operationType: BulkEditOptionType;
  setOperations: React.Dispatch<React.SetStateAction<OperationEntry[]>>;
  isSubmitting: boolean;
}): React.ReactElement {
  return (
    <Box mt={4}>
      <Button
        variant="ghost"
        colorScheme="gray"
        leftIcon={<AddIcon />}
        isDisabled={isSubmitting}
        onClick={() =>
          setOperations((currentOperations) => [
            ...currentOperations,
            { type: operationType, id: uniqueId() },
          ])
        }
      >
        Add row
      </Button>
    </Box>
  );
}

export function validateOperation(operation: OperationEntry): boolean {
  switch (operation.type) {
    case "ledger":
      return !!operation.from && !!operation.to;

    case "asset":
      return !!operation.from && !!operation.to;

    case "wallet":
      return !!operation.from && !!operation.to;

    case "time":
      return !!operation.hours || !!operation.minutes || !!operation.seconds;
  }
}
